import React, { FC, useState, useMemo, useEffect } from 'react';
import { Button, Spin } from 'antd';
import { generatePath, useHistory } from 'react-router-dom';
import {
  CreditCardOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  QrcodeOutlined,
  RightOutlined,
} from '@ant-design/icons';

import Modal from '../Modal/Modal';
import { EripContent, IErip } from '../PaymentModal/PaymentModal';
import { APP_URL, ENDPOINT } from '../../constants/common-app.const';
import useGetMyContracts, { MyCourseCard } from '../../hooks/useGetMyContracts';
import api from '../../utils/api.util';
import toast from '../../utils/toast.util';

import css from './MyCoursesCards.module.css';

type PaymentMethod = 'erip' | 'card' | 'requisites';

const PAYMENT_METHOD_MAP: Record<PaymentMethod, string> = {
  card: 'alfabank',
  erip: 'erip',
  requisites: 'manual',
};

const PAYMENT_METHODS: { value: PaymentMethod; label: string; icon: React.ReactNode }[] = [
  { value: 'erip', label: 'ЕРИП', icon: <QrcodeOutlined /> },
  { value: 'card', label: 'Оплата картой', icon: <CreditCardOutlined /> },
  { value: 'requisites', label: 'Оплата по реквизитам', icon: <FileTextOutlined /> },
];

type CourseStatus = 'new' | 'created' | 'payed' | 'finished';

const STATUS_LABELS: Record<CourseStatus, string> = {
  new: 'Новый',
  created: 'Создан',
  payed: 'Оплачен',
  finished: 'Завершен',
};

const STATUS_CLASSES: Record<CourseStatus, string> = {
  new: css.StatusNew,
  created: css.StatusCreated,
  payed: css.StatusPayed,
  finished: css.StatusFinished,
};

const PAGE_SIZE = 4;

interface Props {
  statusFilter?: string;
  search?: string;
  onCount?: (n: number) => void;
}

const MyCoursesCards: FC<Props> = ({ statusFilter = 'all', search = '', onCount }) => {
  const history = useHistory();
  const { contracts, loading, refresh } = useGetMyContracts();
  const [payModalCard, setPayModalCard] = useState<MyCourseCard | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('erip');
  const [payLoading, setPayLoading] = useState(false);
  const [payConfirmMessage, setPayConfirmMessage] = useState<string | null>(null);
  const [eripData, setEripData] = useState<IErip | null>(null);

  const filtered = useMemo(() => {
    let result = contracts;
    if (statusFilter && statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.contractNumber.toLowerCase().includes(q),
      );
    }
    return result;
  }, [contracts, statusFilter, search]);

  useEffect(() => {
    if (!loading && onCount) onCount(filtered.length);
  }, [filtered.length, loading, onCount]);

  const [page, setPage] = useState(1);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [statusFilter, search]);

  const PAYMENT_CONFIRM: Record<PaymentMethod, string | null> = {
    erip: null,
    requisites: 'Счёт на оплату отправлен на вашу электронную почту. После оплаты отправьте подтверждение на office@rabt.by — доступ к курсу будет открыт в течение рабочего дня.',
    card: null,
  };

  const availableMethods = PAYMENT_METHODS.filter((m) =>
    payModalCard?.clientType === 'individual'
      ? m.value !== 'requisites'
      : m.value === 'requisites',
  );

  const openPayModal = (card: MyCourseCard) => {
    setPayModalCard(card);
    setPaymentMethod(card.clientType === 'individual' ? 'erip' : 'requisites');
    setPayConfirmMessage(null);
    setEripData(null);
  };

  const handlePay = async () => {
    if (!payModalCard) return;
    setPayLoading(true);
    try {
      const result = await api.postJson(
        `${ENDPOINT.CONTRACTS}/${payModalCard.id}/payments`,
        { method: PAYMENT_METHOD_MAP[paymentMethod] }
      );
      if (paymentMethod === 'card') {
        const formUrl = result?.result?.formUrl;
        if (formUrl) {
          window.location.href = formUrl;
          return;
        }
        toast.showOnFailure('Не удалось получить ссылку на оплату');
        return;
      }
      if (paymentMethod === 'erip') {
        const r = result?.result;
        setEripData({
          accountNumber: r?.eripAccountNumber ?? '',
          invoiceNumber: r?.eripInvoiceNumber ?? 0,
          amountToPay: r?.amountToPay ?? 0,
          eripQRCodeLink: r?.eripQRCodeLink ?? '',
        });
        refresh();
        return;
      }
      const msg = PAYMENT_CONFIRM[paymentMethod];
      if (msg) {
        setPayConfirmMessage(msg);
      } else {
        setPayModalCard(null);
      }
      refresh();
    } catch {
      toast.showOnFailure('Ошибка инициализации оплаты');
    } finally {
      setPayLoading(false);
    }
  };

  const goToCourse = (id: string) =>
    history.push(generatePath(APP_URL.COURSE, { id }));

  const resumeEnrollment = (card: MyCourseCard) => {
    if (!card.courseId) return;
    history.push(
      APP_URL.COURSE_ENROLLMENT.replace(':id', card.courseId),
      { existingContractId: card.id, title: card.title },
    );
  };

  const handleViewContract = async (card: MyCourseCard) => {
    try {
      const html = await api.getText(`${ENDPOINT.CONTRACTS}/${card.uuid}`);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.showOnFailure('Не удалось загрузить договор');
    }
  };

  if (loading) {
    return (
      <div className={css.Wrapper}>
        <Spin />
      </div>
    );
  }

  if (!filtered.length) {
    return (
      <div className={css.Wrapper}>
        {contracts.length === 0
          ? 'У вас нет договоров. Перейдите в раздел «Курсы», чтобы оформить договор.'
          : 'Договоров по заданным фильтрам не найдено.'}
      </div>
    );
  }

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const visible = filtered.slice(start, start + PAGE_SIZE);
  const shown = visible.length;

  return (
    <div className={css.Wrapper}>
      {visible.map((card: MyCourseCard) => {
        const showResume = card.status === 'new';
        const showStart = card.status === 'payed' || card.status === 'finished';

        return (
          <div key={card.id} className={css.Card}>
            <div className={css.Title}>{card.title}</div>

            <div className={css.Body}>
              <div className={css.Fields}>
                <div className={css.Field}>
                  <div className={css.Label}>Дата создания</div>
                  <div className={css.Value}>{card.createdAt}</div>
                </div>

                <div className={css.Field}>
                  <div className={css.Label}>Период группы</div>
                  <div className={css.Value}>
                    {card.periodFrom} — {card.periodTo}
                  </div>
                </div>

                <div className={css.Field}>
                  <div className={css.Label}>Сумма</div>
                  <div className={css.Value}>{card.amount}</div>
                </div>

                <div className={css.Field}>
                  <div className={css.Label}>№ договора</div>
                  <div className={css.Value}>{card.contractNumber}</div>
                </div>

                <div className={css.Field}>
                  <div className={css.Label}>Слушателей</div>
                  <div className={css.Value}>{card.studentsCount}</div>
                </div>

                <div className={css.Field}>
                  <div className={css.Label}>Статус договора</div>
                  <div>
                    <span
                      className={`${css.StatusBadge} ${
                        STATUS_CLASSES[card.status]
                      }`}
                    >
                      {STATUS_LABELS[card.status]}
                    </span>
                  </div>
                </div>
              </div>

              <div className={css.Actions}>
                {card.isOwner && (
                  <Button
                    className={css.ActionBtn}
                    onClick={() => handleViewContract(card)}
                  >
                    Посмотреть договор
                  </Button>
                )}
                {showResume && (
                  <Button
                    className={css.ActionBtn}
                    type='primary'
                    onClick={() => resumeEnrollment(card)}
                  >
                    Продолжить оформление
                  </Button>
                )}
                {card.status === 'created' && card.isOwner && (
                  <Button
                    className={css.ActionBtn}
                    type='primary'
                    onClick={() => openPayModal(card)}
                  >
                    Оплатить
                  </Button>
                )}
                {showStart && (
                  <Button
                    className={css.ActionBtn}
                    onClick={() => goToCourse(card.id)}
                  >
                    {card.listenerCompleted ? 'Пройти повторно' : 'Пройти курс'}
                  </Button>
                )}
              </div>
            </div>

            {card.notice && (
              <div className={css.Notice}>
                <InfoCircleOutlined className={css.NoticeIcon} />
                <span>{card.notice}</span>
              </div>
            )}
          </div>
        );
      })}

      <Modal
        display={!!payModalCard}
        onClose={() => {
          if (!payLoading) {
            setPayModalCard(null);
            setPayConfirmMessage(null);
            setEripData(null);
          }
        }}
        title={
          eripData
            ? `Оплатите счёт: ${eripData.invoiceNumber}`
            : payConfirmMessage
            ? 'Заявка принята'
            : 'Выберите способ оплаты'
        }
      >
        {eripData ? (
          <EripContent
            eripCode={eripData}
            onClose={() => setEripData(null)}
            onSuccess={() => { setPayModalCard(null); setEripData(null); }}
          />
        ) : payConfirmMessage ? (
          <>
            <p className={css.PayConfirmText}>{payConfirmMessage}</p>
            <div className={css.PayModalFooter}>
              <Button type='primary' onClick={() => { setPayModalCard(null); setPayConfirmMessage(null); }}>
                Понятно
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={css.PayMethodGrid}>
              {availableMethods.map((m) => (
                <button
                  key={m.value}
                  type='button'
                  className={`${css.PayMethodCard} ${paymentMethod === m.value ? css.PayMethodCardActive : ''}`}
                  onClick={() => setPaymentMethod(m.value)}
                >
                  <span className={css.PayMethodIcon}>{m.icon}</span>
                  {m.label}
                </button>
              ))}
            </div>
            <div className={css.PayModalFooter}>
              <Button onClick={() => setPayModalCard(null)} disabled={payLoading}>
                Отмена
              </Button>
              <Button type='primary' loading={payLoading} disabled={payLoading} onClick={handlePay}>
                Оплатить
              </Button>
            </div>
          </>
        )}
      </Modal>

      <div className={css.Pagination}>
        <span className={css.PaginationInfo}>
          Показано {shown} / {total}
        </span>

        <div className={css.PageControls}>
          <button
            type='button'
            className={css.PageBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <LeftOutlined />
          </button>
          <span className={css.PageNumber}>{page}</span>
          <button
            type='button'
            className={css.PageBtn}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            <RightOutlined />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyCoursesCards;
